/**
 * Material handed to this app by the system «Поделиться».
 *
 * Inert everywhere except inside the two shells: a page cannot be a share
 * target, and the PWA's own `share_target` was deliberately not built (see the
 * handoff). `hasNativeShare` is the switch, and it is false unless the plugin is
 * really registered — a bundle newer than the shell around it has to find that
 * out rather than call into something that will never answer.
 *
 * **Drained, never announced.** A share arrives while the app is launching —
 * often before the web view has run a line — so the shell queues it and this
 * asks. That is the same arrangement a notification tap is under, and for the
 * same reason: a design built on `notifyListeners` would work perfectly for
 * every case except the one somebody actually performed. It is also what makes
 * «материал ждёт через экран логина» free: ChatContext, which drains, is
 * mounted inside RequireAuth and does not exist until somebody has signed in.
 *
 * Unlike the notification queue, the shell's copy of this survives being killed
 * — the files are already on disk, and losing the note beside them would leave
 * bytes nobody will ever sweep and a photograph that silently never arrived.
 *
 * The other ends are `ShareTargetPlugin.kt` and `ShareTargetPlugin.swift`, kept in step by
 * hand.
 */
import { apiOrigin, isNative, sessionToken } from '../api/origin.ts';
import { withTimeout, PLUGIN_TIMEOUT_MS } from './gallery.ts';
import type { SharedPayload } from './share.ts';

/**
 * The plugin's name on both sides.
 *
 * `ShareTarget` rather than `Share`, which is taken: `@capacitor/share` is on
 * the bridge under that name and is how lib/download.ts hands a file *out* of
 * the app. The two are opposite directions of one word, and one of them had to
 * give.
 */
export const SHARE_PLUGIN = 'ShareTarget';

interface NativeSharePlugin {
  /**
   * The server and this session's bearer, for the half of the app that has no
   * WebView to read them from — the iOS share sheet runs in its own process and
   * posts the message itself. Android takes it too and ignores it.
   */
  configure?(options: { origin: string; token: string }): Promise<void>;
  /**
   * Everything that has arrived and not yet been handed over. Empties the
   * queue, so two callers would see one share each — there is one caller.
   */
  drain?(): Promise<{ shares: SharedPayload[] }>;
  /**
   * This arrival is finished with, whether it was sent or cancelled: the shell
   * may delete the files it copied.
   */
  release?(options: { id: string }): Promise<void>;
  /**
   * The crosses. On Android this puts the task back where it came from, so the
   * app the material was shared from is what returns to the screen; on iOS
   * there is no such thing and it does nothing.
   */
  dismiss?(): Promise<void>;
}

interface CapacitorBridge {
  isPluginAvailable?: (name: string) => boolean;
  Plugins?: Record<string, unknown>;
}

function bridge(): CapacitorBridge | undefined {
  return (globalThis as { Capacitor?: CapacitorBridge }).Capacitor;
}

/** True only inside a shell that really carries the plugin. */
export const hasNativeShare: boolean = (() => {
  if (!isNative) return false;
  return bridge()?.isPluginAvailable?.(SHARE_PLUGIN) === true;
})();

/**
 * Read straight out of `window.Capacitor.Plugins`, synchronously — the dynamic
 * `import('@capacitor/core')` this would otherwise be never settles inside the
 * shell. Same reasoning as `nativePushPlugin`.
 */
function plugin(): NativeSharePlugin | null {
  if (!hasNativeShare) return null;
  return (bridge()?.Plugins?.[SHARE_PLUGIN] as NativeSharePlugin | undefined) ?? null;
}

/**
 * Whatever the shell has been holding. `[]` off the phone, and `[]` rather than
 * a throw when the plugin misbehaves: a share that cannot be collected is a
 * share that did not happen, and the chat list behind it must still come up.
 */
export async function drainNativeShares(): Promise<SharedPayload[]> {
  const share = plugin();
  if (!share?.drain) return [];
  try {
    const { shares } = await withTimeout(share.drain(), PLUGIN_TIMEOUT_MS, 'share');
    return shares ?? [];
  } catch (error) {
    console.warn('share: drain failed', error);
    return [];
  }
}

/**
 * Hands the shell the credential its share sheet posts with.
 *
 * Called on every launch and on every return to the app, beside the drain — the
 * token is the session id and outlives nothing, so re-stating it is cheaper
 * than reasoning about when it changed. `forgetNativeShare` is the other half
 * and every path that ends a session owes it one: a bearer that outlives its
 * session is the one thing this arrangement must not do (see
 * `configureNativePush`, which is under the same rule for the same reason).
 */
export async function configureNativeShare(): Promise<void> {
  const share = plugin();
  if (!share?.configure) return;
  try {
    await withTimeout(
      share.configure({ origin: apiOrigin, token: sessionToken() ?? '' }),
      PLUGIN_TIMEOUT_MS,
      'share',
    );
  } catch (error) {
    console.warn('share: could not configure the native half', error);
  }
}

/** The credential goes with the session it belonged to. */
export async function forgetNativeShare(): Promise<void> {
  try {
    await plugin()?.configure?.({ origin: '', token: '' });
  } catch (error) {
    console.warn('share: could not clear the native half', error);
  }
}

/** Says the shell may delete what it copied. Never worth failing over. */
export async function releaseNativeShare(id: string): Promise<void> {
  try {
    await plugin()?.release?.({ id });
  } catch (error) {
    console.warn('share: release failed', error);
  }
}

/** Leaves for wherever the material came from, having sent nothing. */
export async function dismissNativeShare(): Promise<void> {
  try {
    await plugin()?.dismiss?.();
  } catch (error) {
    console.warn('share: dismiss failed', error);
  }
}

/**
 * A shared file as something that can be uploaded.
 *
 * The path is turned into a URL the web view may read and fetched from disk.
 * The byte length is what says it worked, not the status: Capacitor's asset
 * handler answers a *media* extension with a response carrying no status line
 * at all, so a video that arrived whole reaches `fetch` as `status: 0,
 * ok: false` — the same trap `blobFrom` in nativeVideoNote.ts documents, and
 * the reason this does not check `response.ok`.
 */
export async function fileFromShare(item: {
  path: string;
  name: string;
  mime: string;
}): Promise<File> {
  const convert = (globalThis as { Capacitor?: { convertFileSrc?: (path: string) => string } })
    .Capacitor?.convertFileSrc;
  const url = convert ? convert(item.path) : item.path;
  const response = await fetch(url);
  const blob = await response.blob();
  if (blob.size === 0) throw new Error(`Не удалось прочитать «${item.name}»`);
  return new File([blob], item.name, { type: item.mime || blob.type });
}
